import React, {useCallback, useEffect, useRef, useState} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {
    ApTimelineAudioWaveform,
    base64To,
} from '@accurate-player/accurate-player-components-external';
import {getQCJobMediaAudioWaveform} from '@pluto/rest';
import {FrameRate} from '@accurate-player/accurate-player-core';

import {getWaveformStyling} from 'VideoAudioQC/Timeline/TimelineTheme';
import Waveform from './Waveform';

export interface WaveformContainerProps {
    url: string;
    enabled: boolean;
    frameRate: FrameRate;
}

export type WaveformRange = {
    start: number,
    end: number,
}

export type LoadDataParams = {
    range: WaveformRange,
    width: number,
}

export type LoadDataParamsOrNull = LoadDataParams | null;
export type ApTimelineAudioWaveformOrNull = ApTimelineAudioWaveform | null;
export type WaveformRef = React.MutableRefObject<ApTimelineAudioWaveformOrNull>;
export type SetLoadData = React.Dispatch<React.SetStateAction<LoadDataParamsOrNull>>;

export type StateAndProps = {
    waveformRef: WaveformRef,
    loadData: LoadDataParamsOrNull,
    setLoadData: SetLoadData,
    props: WaveformContainerProps,
};

const equalRange = (a: WaveformRange, b: WaveformRange) => {
    return a.start === b.start && a.end === b.end;
};

export const equalLoadData = (a: LoadDataParamsOrNull, b: LoadDataParamsOrNull) => {
    if (a === null && b === null)
        return true;
    
    if (a !== null && b !== null) {
        return a.width === b.width && equalRange(a.range, b.range);
    }
    
    return false;
};

export const updateLoadData = curry((newData: LoadDataParamsOrNull, currentData: LoadDataParamsOrNull): LoadDataParamsOrNull => {
    if (equalLoadData(newData, currentData)) {
        return currentData;
    }
    
    return newData;
});

export const handleDataRequest = curry((setLoadData: SetLoadData, range: WaveformRange, width: number) => {
    const data: LoadDataParams = {
        range,
        width,
    };
    
    const handleUpdateLoadData = updateLoadData(data);
    setLoadData(handleUpdateLoadData);
});

export const handleWaveformChange = curry((stateAndProps: StateAndProps, node: ApTimelineAudioWaveform) => {
    const {
        waveformRef,
        setLoadData,
        props,
    } = stateAndProps;
    const {enabled} = props;
    
    waveformRef.current = node;
    
    if (!node || !node.api)
        return;
    
    const styling = getWaveformStyling(enabled);
    node.waveformStyle = styling;
    node.apDataRequest = handleDataRequest(setLoadData);
});

const useWaveformRef = (stateAndProps: StateAndProps) => {
    const {props, loadData} = stateAndProps;
    const {enabled} = props;
    const onWaveformChange = handleWaveformChange(stateAndProps);
    const setRef = useCallback(onWaveformChange, [enabled, loadData]);
    
    return [setRef];
};

export const onLoadDataChange = wrap((stateAndProps: StateAndProps) => {
    const {
        waveformRef,
        props,
        loadData,
    } = stateAndProps;
    const {
        frameRate,
        url,
    } = props;
    
    if (!loadData)
        return;
    
    fetchWaveformData({
        waveformRef,
        url,
        frameRate,
        loadData,
    });
});

export type FetchWaveformProps = {
    waveformRef: WaveformRef,
    url: string,
    frameRate: FrameRate,
    loadData: LoadDataParams,
};

export const fetchWaveformData = async (props: FetchWaveformProps) => {
    const {
        waveformRef,
        url,
        frameRate,
        loadData,
    } = props;
    const {
        range,
        width,
    } = loadData;
    const {
        start,
        end,
    } = range;
    const {
        numerator,
        denominator,
    } = frameRate;
    
    if (!waveformRef.current || end === 0)
        return;
    
    const frameRateValue = numerator / denominator;
    
    // convert frames to milliseconds
    const from = Math.round(start / frameRateValue * 1000);
    const to = Math.round(end / frameRateValue * 1000);
    
    try {
        const {data} = await getQCJobMediaAudioWaveform(url, {
            from,
            to,
            samples: Math.round(width * 10),
        });
        
        const sampleData = await base64To(Int8Array, data);
        const samples = Array.from(sampleData);
        
        waveformRef.current.api.addSampleRange({
            start,
            end,
            samples,
        });
    } catch(error) { return error; }
};

export default function WaveformContainer(props: WaveformContainerProps) {
    const [loadData, setLoadData] = useState<LoadDataParamsOrNull>(null);
    const waveformRef = useRef<ApTimelineAudioWaveformOrNull>(null);
    const stateAndProps: StateAndProps = {
        waveformRef,
        props,
        loadData,
        setLoadData,
    };
    const [setWaveformRef] = useWaveformRef(stateAndProps);
    const handleLoadDataChange = onLoadDataChange(stateAndProps);
    useEffect(handleLoadDataChange, [loadData]);
    
    return (
        <Waveform
            setWaveformRef={setWaveformRef}
        />
    );
}
